import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Box, Typography, IconButton } from "@mui/material";
import KeyboardArrowLeft from "../../components/icon-components/KeyboardArrowLeft";
import {
  PAGE_HEADER_EDIT_ADMIN,
  PLACEHOLDER_ROLE,
  PLACEHOLDER_STATUS,
} from "../../utility/constants/locale";
import CustomDropdown from "../../components/custom-dropdown/CustomDropdown";
import CustomTextField from "../../components/custom-text-field/CustomTextField";
import CustomButton from "../../components/custom-button/CustomButton";
import Grid from "@mui/material/Grid2";
import {
  BTN_CANCEL,
  FORM_LABEL_ADMIN_ROLE,
  FORM_LABEL_ADMIN_NAME,
  FORM_LABEL_ADMIN_EMAIL_ID,
  FORM_LABEL_USER_STATUS,
  BTN_SAVE_UPDATE,
} from "../../utility/constants/locale";
import { useSnackbar } from "notistack";
import { useAppSelector } from "../../store/hooks";
import { useUpdateAdminUserMutation } from "./UpdateAdminUserAPISlice";
import CustomSpinner from "../../components/custom-spinner/CustomSpinner";
import {
  getDropdownDataForCategory,
  segregateCategoryWiseStatus,
} from "../../utility/functions/common-functions";
import { CATEGORY_ID_MAPPING } from "../../utility/constants/common-constants";

const EditAdmin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const accessToken = useAppSelector(
    (state) => state.adminAuth.user?.accessToken
  );
  const userName = useAppSelector((state) => state.adminAuth.user?.name);
  const userRoles = useAppSelector((state) => state.adminMasterData.userRoles);
  const categoryWiseStatus = useAppSelector(
    (state) => state.adminMasterData.categoryWiseStatus
  );
  const [roleDesc, setRoleDesc] = useState(" ");
  const [roleName, setRoleName] = useState("");
  useEffect(() => {
    const roleNameSelected = userRoles.find(
      (role) => role.value === initialRoleId
    );
    setRoleName(roleNameSelected?.label || "");
    setRoleDesc(roleNameSelected?.desc || " ");
  }, []); // No dependency array to run only once

  const [userId, setUserId] = useState(location?.state?.userId);

  const [roleId, setRoleId] = useState<number>(
    Number(location?.state?.userRoleId) || 0
  );
  const [statusId, setStatusId] = useState(
    Number(location?.state?.statusId) || 0
  );
  const [displayName, setDisplayName] = useState(
    location?.state?.displayName || ""
  );
  const [emailId, setEmailId] = useState(location?.state?.emailId || "");
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  const [initialRoleId] = useState<number>(
    Number(location?.state?.userRoleId) || 0
  );
  const [initialStatusId] = useState(location?.state?.statusId || "0");
  const handleInputChange = () => {};

  const handleStatusDropDownChange = (name: string, value: string | number) => {
    setStatusId(value as number);
    setIsSaveEnabled(roleId !== initialRoleId || value !== initialStatusId);
  };

  const handleRoleDropDownChange = (name: string, value: string | number) => {
    setRoleId(value as number);
    setIsSaveEnabled(value !== initialRoleId || statusId !== initialStatusId);

    const roleNameSelected = userRoles.find(
      (role) => role.value === (value as number)
    );
    setRoleName(roleNameSelected?.label || "");
    setRoleDesc(roleNameSelected?.desc || " ");
  };

  const groupedCategoryStatus = segregateCategoryWiseStatus(categoryWiseStatus);
  const statusDropdownData = getDropdownDataForCategory(
    groupedCategoryStatus,
    CATEGORY_ID_MAPPING.adminProfile,
    false
  );

  const [updateMilestoneTask, { isLoading, isSuccess, isError, error }] =
    useUpdateAdminUserMutation();

  const navigateBack = () => {
    navigate("/admin", { replace: true });
  };

  const updateUser = async (values: any) => {
    if (roleId > 0 && statusId > 0) {
      try {
        const body = {
          userId: userId,
          mail: emailId,
          roleId: roleId,
          statusId: statusId,
          updatedBy: userName || "SYSTEM",
        };

        const response = await updateMilestoneTask({
          body,
          accessToken,
        }).unwrap();

        navigate("/admin");

        enqueueSnackbar("User Details updated successfully", {
          variant: "success",
        });
      } catch (error) {
        console.error("Failed to update user Details :", error);
        enqueueSnackbar("Failed to update user Details ", { variant: "error" });
      }
    } else {
      if (roleId === 0 && statusId === 0) {
        enqueueSnackbar("Select Role & Status to update user details ", {
          variant: "warning",
        });
      } else if (statusId === 0) {
        enqueueSnackbar("Select Status to update user details ", {
          variant: "warning",
        });
      } else if (roleId === 0) {
        enqueueSnackbar("Select Role to update user details ", {
          variant: "warning",
        });
      }
    }
  };

  if (isLoading) {
    return <CustomSpinner />;
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        alignItems="center"
        mb={2}
        mt={2}
      >
        <IconButton
          onClick={navigateBack}
          aria-label="back button"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Typography variant="h2">{PAGE_HEADER_EDIT_ADMIN}</Typography>
      </Box>
      <Grid
        container
        spacing={6}
        mb={2}
        sx={{ paddingLeft: "60px", paddingRight: "60px" }}
      >
        <Grid size={4}>
          <CustomTextField
            inputFormTitle={FORM_LABEL_ADMIN_NAME}
            value={displayName}
            placeholder="User Name"
            changeTrigger={handleInputChange}
            isRequired={true}
          />
        </Grid>
        <Grid size={4}>
          <CustomTextField
            inputFormTitle={FORM_LABEL_ADMIN_EMAIL_ID}
            value={emailId}
            placeholder="Email ID"
            changeTrigger={handleInputChange}
            isRequired={true}
          />
        </Grid>

        <Grid size={4}>
          <CustomDropdown
            inputFormTitle={FORM_LABEL_ADMIN_ROLE}
            value={roleId}
            changeTrigger={(value) => handleRoleDropDownChange("role", value)}
            options={userRoles}
            isRequired
            placeholder={PLACEHOLDER_ROLE}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={6}
        mb={2}
        sx={{ paddingLeft: "60px", paddingRight: "60px" }}
      >
        <Grid size={4}>
          <CustomDropdown
            inputFormTitle={FORM_LABEL_USER_STATUS}
            value={statusId}
            changeTrigger={(value) =>
              handleStatusDropDownChange("status", value)
            }
            options={statusDropdownData}
            isRequired
            placeholder={PLACEHOLDER_STATUS}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
        mb={2}
        sx={{ paddingLeft: "60px", paddingRight: "60px", marginTop: "40px" }}
      >
        <CustomButton
          variant="outlined"
          onClick={navigateBack}
          text={BTN_CANCEL}
        />
        <CustomButton
          type="submit"
          variant="contained"
          color="primary"
          onClick={updateUser}
          text={BTN_SAVE_UPDATE}
          disabled={!isSaveEnabled} // Disable button if no changes
        />
      </Grid>
      {roleName === "Select Role" ? (
        ""
      ) : (
        <Grid
          container
          spacing={1}
          sx={{ paddingLeft: "60px", paddingRight: "60px" }}
        >
          <Grid
            size={12}
            sx={{ display: "flex", justifyContent: "left" }}
          >
            <Typography sx={{ fontWeight: "600", paddingRight: "10px" }}>
              {roleName}
            </Typography>
            <Typography>{roleDesc}</Typography>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default EditAdmin;
